import { useMemo, useState } from 'react';
import { LISTE_ALLE, TablePager, useSeiten } from '../components/TablePager';
import { FormError } from '../components/FormError';
import { keepPreviousData, useQueryClient } from '@tanstack/react-query';
import { useDebouncedValue } from '@mantine/hooks';
import { useNavigate } from '@tanstack/react-router';
import {
  Badge,
  Button,
  Group,
  Modal,
  Select,
  Stack,
  Switch,
  Table,
  TagsInput,
  Text,
  TextInput,
} from '@mantine/core';
import { useForm } from '@mantine/form';
import {
  ApiError,
  useCustomersControllerList,
  useCustomersControllerCreate,
  getCustomersControllerListQueryKey,
} from '@werkbank/shared';
import { CHANNEL_LABELS, selectOptions } from '../lib/format';
import { useNeuParam } from '../lib/useNeuParam';
import { useGemerkt } from '../lib/gemerkt';
// Vollanalyse S6: localStorage über den gemeinsamen try/catch-sicheren Helfer.
import { readJsonLocal, writeJsonLocal } from '../lib/storage';
import { Icon } from '../components/navIcons';
import { listMeta } from '../components/listPageContract';
import { ListPage } from '../components/ListPage';
import { listState } from '../components/listPageContract';
import { TableCard } from '../components/TableCard';
import { EmptyState } from '../components/EmptyState';
import { PlzVorschlag } from '../components/PlzVorschlag';

interface CustomerFormValues {
  kind: 'COMPANY' | 'PRIVATE';
  salutation: string;
  displayName: string;
  legalName: string;
  street: string;
  zip: string;
  city: string;
  email: string;
  phone: string;
  invoiceChannel: 'XRECHNUNG_EMAIL' | 'ZUGFERD_EMAIL' | 'PDF_EMAIL' | 'PORTAL_MANUAL';
  requiredRefs: string[];
}

// Sortierbare Spalten der Kundenliste. Die Wahl überlebt Logout/Reload, weil
// sie in localStorage liegt (nicht an die Session gebunden).
type SortCol = 'legacySageId' | 'displayName' | 'invoiceChannel' | 'datevDebtorNo';
type SortState = { col: SortCol; dir: 'asc' | 'desc' };
const CUSTOMER_SORT_KEY = 'wb.customers.sort';

function readSort(): SortState {
  const p = readJsonLocal(CUSTOMER_SORT_KEY) as SortState | null;
  if (p && typeof p.col === 'string' && (p.dir === 'asc' || p.dir === 'desc')) return p;
  return { col: 'displayName', dir: 'asc' };
}
function writeSort(s: SortState): void {
  writeJsonLocal(CUSTOMER_SORT_KEY, s);
}

export function CustomersPage() {
  // Tobias 2026-09-25: Filter bleiben beim Wechsel der Seite erhalten.
  const [search, setSearch] = useGemerkt('kunden.suche', '');
  const [showArchived, setShowArchived] = useGemerkt('kunden.archiv', false);
  const [createOpen, setCreateOpen] = useState(false);
  const [sort, setSort] = useState<SortState>(readSort);
  const navigate = useNavigate();
  useNeuParam(() => setCreateOpen(true));
  const queryClient = useQueryClient();

  // Vollanalyse S5 (M19-Muster): die Suche läuft serverseitig (die API kappt
  // bei 200) — debounced, damit nicht jeder Tastendruck eine Anfrage auslöst;
  // placeholderData hält die letzte Liste sichtbar (kein Flackern beim Tippen).
  const [debouncedSearch] = useDebouncedValue(search.trim(), 250);
  const customers = useCustomersControllerList(
    {
      search: debouncedSearch || undefined,
      archived: showArchived ? 'all' : 'false',
      take: LISTE_ALLE,
    },
    { query: { placeholderData: keepPreviousData } },
  );

  const toggleSort = (col: SortCol) =>
    setSort((s) => {
      const next: SortState =
        s.col === col ? { col, dir: s.dir === 'asc' ? 'desc' : 'asc' } : { col, dir: 'asc' };
      writeSort(next);
      return next;
    });

  // Clientseitig sortieren (die Liste lädt vollständig). Kundennummer und
  // Debitor numerisch-natürlich, Text nach deutscher Kollation.
  const sortedRows = useMemo(() => {
    const rows = customers.data ? [...customers.data] : [];
    const { col, dir } = sort;
    const mul = dir === 'asc' ? 1 : -1;
    const num = (v: number | null | undefined) => v ?? null;
    rows.sort((a, b) => {
      let d: number;
      if (col === 'datevDebtorNo') {
        const av = num(a.datevDebtorNo);
        const bv = num(b.datevDebtorNo);
        d = av === bv ? 0 : av === null ? 1 : bv === null ? -1 : av - bv;
      } else {
        const av = String((a[col] as string | null) ?? '');
        const bv = String((b[col] as string | null) ?? '');
        d = av.localeCompare(bv, 'de', { numeric: true, sensitivity: 'base' });
      }
      return mul * d;
    });
    return rows;
  }, [customers.data, sort]);

  const sortableTh = (col: SortCol, label: string) => (
    <Table.Th
      style={{ cursor: 'pointer', userSelect: 'none' }}
      onClick={() => toggleSort(col)}
      data-testid={`customers-sort-${col}`}
    >
      <Group gap={4} wrap="nowrap">
        <span>{label}</span>
        <span
          aria-hidden
          style={{
            display: 'inline-flex',
            color: 'var(--wb-primary-600)',
            opacity: sort.col === col ? 1 : 0,
            transform: sort.col === col && sort.dir === 'desc' ? 'rotate(90deg)' : 'rotate(-90deg)',
          }}
        >
          <Icon name="chevron" size={12} />
        </span>
      </Group>
    </Table.Th>
  );

  const create = useCustomersControllerCreate<ApiError>({
    mutation: {
      onSuccess: (customer) => {
        void queryClient.invalidateQueries({ queryKey: getCustomersControllerListQueryKey() });
        setCreateOpen(false);
        void navigate({ to: '/customers/$customerId', params: { customerId: customer.id } });
      },
    },
  });

  const form = useForm<CustomerFormValues>({
    initialValues: {
      kind: 'COMPANY',
      salutation: '',
      displayName: '',
      legalName: '',
      street: '',
      zip: '',
      city: '',
      email: '',
      phone: '',
      // Tobias 2026-10-04: XRechnung als Standard, je Kunde änderbar.
      invoiceChannel: 'XRECHNUNG_EMAIL',
      requiredRefs: [],
    },
    validate: {
      displayName: (v) => (v.trim() ? null : 'Pflichtfeld'),
      street: (v) => (v.trim() ? null : 'Pflichtfeld'),
      zip: (v) => (v.trim() ? null : 'Pflichtfeld'),
      city: (v) => (v.trim() ? null : 'Pflichtfeld'),
    },
  });

  const submit = form.onSubmit((v) => {
    create.mutate({
      data: {
        kind: v.kind,
        salutation: v.salutation.trim() || undefined,
        displayName: v.displayName.trim(),
        legalName: v.legalName.trim() || undefined,
        billingAddress: { street: v.street.trim(), zip: v.zip.trim(), city: v.city.trim() },
        email: v.email.trim() || undefined,
        phone: v.phone.trim() || undefined,
        invoiceChannel: v.invoiceChannel,
        // P5-A §1: Pflichtreferenzen sind ein geschäftsspezifisches Feld — bei
        // Privatkunden nicht erhoben.
        requiredRefs: v.kind === 'COMPANY' ? v.requiredRefs : [],
      },
    });
  });

  // D-W1 QS-3: der Zähler steht nur noch im Kartenfuß; die Kopf-Meta trägt
  // Beschreibung und aktive Filter (Vollanalyse S6: gemeinsamer listMeta-Helfer).
  const meta = listMeta(
    !!customers.data,
    'Alle Kunden des Betriebs',
    showArchived ? 'inkl. archivierte' : null,
    search ? `Suche „${search}“` : null,
  );

  // D-W1 Welle 2 (A2-13, W2-B4): Seite auf das ListPage-Gerüst migriert —
  // die vier Zustände (Laden/Fehler/Leer/Gefüllt) trägt das Gerüst.
  const seiten = useSeiten(sortedRows, 'kunden');
  return (
    <>
      <ListPage
        title="Kunden"
        meta={meta}
        action={
          <Button onClick={() => setCreateOpen(true)} data-testid="customer-create">
            Neuer Kunde
          </Button>
        }
        filters={
          <>
            <TextInput
              placeholder="Suche (Name, E-Mail, Kundennr.)…"
              value={search}
              onChange={(e) => setSearch(e.currentTarget.value)}
              w={280}
              data-testid="customer-search"
            />
            <Switch
              label="Archivierte anzeigen"
              checked={showArchived}
              onChange={(e) => setShowArchived(e.currentTarget.checked)}
            />
          </>
        }
        state={listState(customers, customers.data?.length === 0)}
        errorText="Kunden konnten nicht geladen werden."
        empty={
          <TableCard testId="customers-card">
            <EmptyState
              text={
                search || showArchived
                  ? 'Keine Kunden für diese Filter gefunden.'
                  : 'Noch keine Kunden angelegt.'
              }
              action={<Button onClick={() => setCreateOpen(true)}>Neuer Kunde</Button>}
            />
          </TableCard>
        }
      >
        <TableCard
          testId="customers-card"
          // Vollanalyse S5: exakt 200 Zeilen = serverseitige Kappung erreicht —
          // Hinweis statt nackter Zählung, damit niemand die Liste für vollständig hält.
          footer={
            <TablePager p={seiten} einzahl="Kunde" mehrzahl="Kunden" testId="customers-pager" />
          }
        >
          <Table highlightOnHover>
            <Table.Thead>
              <Table.Tr>
                {sortableTh('legacySageId', 'Kundennummer')}
                {sortableTh('displayName', 'Name')}
                {sortableTh('invoiceChannel', 'Rechnungskanal')}
                <Table.Th>Pflichtreferenzen</Table.Th>
                {sortableTh('datevDebtorNo', 'Debitor')}
              </Table.Tr>
            </Table.Thead>
            <Table.Tbody>
              {seiten.sichtbar.map((c) => (
                <Table.Tr
                  key={c.id}
                  style={{ cursor: 'pointer', opacity: c.archived ? 0.5 : 1 }}
                  onClick={() =>
                    void navigate({ to: '/customers/$customerId', params: { customerId: c.id } })
                  }
                >
                  <Table.Td className="wb-tnum">{c.legacySageId ?? ''}</Table.Td>
                  <Table.Td>
                    <Group gap="xs" wrap="nowrap">
                      {/* Kundenart als Icon statt eigener Spalte: Privat = Person,
                      Firma = Gebäude (Titel für Screenreader/Tooltip). */}
                      <span
                        title={c.kind === 'COMPANY' ? 'Firma' : 'Privatkunde'}
                        style={{
                          color: 'var(--wb-text-faint)',
                          display: 'inline-flex',
                          flex: 'none',
                        }}
                      >
                        <Icon name={c.kind === 'COMPANY' ? 'building' : 'personSingle'} size={18} />
                      </span>
                      <Text fw={500}>{c.displayName}</Text>
                      {c.archived && (
                        <Badge color="gray" size="xs">
                          archiviert
                        </Badge>
                      )}
                    </Group>
                  </Table.Td>
                  <Table.Td>{CHANNEL_LABELS[c.invoiceChannel] ?? c.invoiceChannel}</Table.Td>
                  <Table.Td>
                    {/* D-W1 KU-1: Referenzen als leiser Chip statt Versalien-Badge. */}
                    {c.requiredRefs.map((r) => (
                      <Badge
                        key={r}
                        className="wb-ref-badge"
                        variant="light"
                        color="gray"
                        size="sm"
                        mr={4}
                      >
                        {r}
                      </Badge>
                    ))}
                  </Table.Td>
                  <Table.Td>{c.datevDebtorNo ?? ''}</Table.Td>
                </Table.Tr>
              ))}
            </Table.Tbody>
          </Table>
        </TableCard>
      </ListPage>

      <Modal opened={createOpen} onClose={() => setCreateOpen(false)} title="Neuer Kunde" size="lg">
        <form onSubmit={submit}>
          <Stack gap="md">
            <Group grow>
              <Select
                label="Art"
                data={[
                  { value: 'COMPANY', label: 'Firma' },
                  { value: 'PRIVATE', label: 'Privat' },
                ]}
                allowDeselect={false}
                {...form.getInputProps('kind')}
              />
              <Select
                label="Rechnungskanal"
                data={selectOptions(CHANNEL_LABELS)}
                allowDeselect={false}
                {...form.getInputProps('invoiceChannel')}
              />
            </Group>
            <TextInput
              label="Anzeigename"
              required
              data-testid="customer-name"
              {...form.getInputProps('displayName')}
            />
            <Group grow>
              <TextInput label="Firmierung (optional)" {...form.getInputProps('legalName')} />
              <TextInput
                label="Anrede (optional)"
                placeholder="z. B. Herr, Frau"
                data-testid="customer-salutation"
                {...form.getInputProps('salutation')}
              />
            </Group>
            <TextInput label="Straße" required {...form.getInputProps('street')} />
            <Group grow>
              <TextInput label="PLZ" required {...form.getInputProps('zip')} />
              <TextInput label="Ort" required {...form.getInputProps('city')} />
            </Group>
            <PlzVorschlag
              street={form.values.street}
              city={form.values.city}
              zip={form.values.zip}
              onUebernehmen={(v) => form.setValues(v)}
            />
            <Group grow>
              <TextInput label="E-Mail (Rechnungseingang)" {...form.getInputProps('email')} />
              <TextInput label="Telefon" {...form.getInputProps('phone')} />
            </Group>
            {/* P5-A §1: Pflichtreferenzen nur bei Geschäftskunden erheben. */}
            {form.values.kind === 'COMPANY' && (
              <TagsInput
                label="Pflichtreferenzen"
                description="z. B. Bestellnummer; wird bei Rechnungen erzwungen"
                data-testid="customer-required-refs"
                {...form.getInputProps('requiredRefs')}
              />
            )}
            <FormError error={create.error} />
            <Group justify="flex-end">
              <Button variant="default" onClick={() => setCreateOpen(false)}>
                Abbrechen
              </Button>
              <Button type="submit" loading={create.isPending} data-testid="customer-save">
                Anlegen
              </Button>
            </Group>
          </Stack>
        </form>
      </Modal>
    </>
  );
}
